import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Stack, ToggleButton, Tooltip } from "@mui/material";
import { Fragment } from "react";
import {
    useAppSidePanelContextProvider
} from "../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { PortfolioModel } from "../../portfolio-info/types/PortfolioModel";
import CompareIcon from '@mui/icons-material/Compare';

export function ComparedPortfolioSelector() {
    const {
        portfolios,
        selectedPortfolio,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        updateSelectedPortfolio,
        saveSelectedPortfolio,
    } = useAppSidePanelContextProvider();

    const availablePortfoliosForComparing = portfolios
        .filter(x => x.name !== selectedPortfolio?.name);
    
    const setComparedPortfolioHandler = async (portfolioModel: PortfolioModel): Promise<void> => {
        setSelectedComparedPortfolioByName(portfolioModel);
        
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }
        
        selectedPortfolio.portfolioSettings.comparedPortfolioId = portfolioModel.id;
        updateSelectedPortfolio();
        saveSelectedPortfolio();
    }

    const handleUseComparedPortfolioChanging = (): void => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.useComparedPortfolio = !selectedPortfolio.portfolioSettings.useComparedPortfolio;
        updateSelectedPortfolio();
        saveSelectedPortfolio();
    }

    async function handleSelection(eventArg: SelectChangeEvent<string>) {
        const selectedPortfolioName = eventArg.target.value;
        const selectedPortfolioModel = portfolios.find(x => x.name === selectedPortfolioName);

        if (selectedPortfolioModel === undefined) {
            return;
        }

        await setComparedPortfolioHandler(selectedPortfolioModel);
    }
    
    const selectorMaxWidth = 300;
    
    return (
        <Stack
            direction="row"
            sx={{
                flex: '1 1 auto',
                minWidth: 0,
                maxWidth: `${selectorMaxWidth}px`,
            }}
            spacing={0.5}
        >
            <Tooltip
                placement="bottom-start"
                disableInteractive
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            maxWidth: '500px'
                        },
                    },
                }}
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 2],
                                },
                            },
                        ],
                    },
                }}
                title={
                    <Fragment>
                        <Box sx={{fontSize: "13px"}}>
                            {
                                selectedPortfolio?.portfolioSettings?.useComparedPortfolio
                                    ? "Hide compared portfolio"
                                    : "Show compared portfolio"
                            }
                        </Box>
                    </Fragment>
                }
            >
                <ToggleButton
                    value="check"
                    selected={selectedPortfolio?.portfolioSettings?.useComparedPortfolio}
                    color="standard"
                    onChange={handleUseComparedPortfolioChanging}
                    sx={{
                        borderColor: "#9e9d9d",
                        height: '5px',
                        width: '5px',
                        '&.Mui-selected': {
                            backgroundColor: '#cdcdcd',
                        },
                    }}
                >
                    <CompareIcon sx={{
                        height: '18px',
                        width: '18px',
                    }} />
                </ToggleButton>
            </Tooltip>
            <FormControl
                variant="standard"
                sx={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Select
                    value={selectedComparedPortfolio
                        ? selectedComparedPortfolio?.name.toString()
                        : ' '}
                    onChange={handleSelection}
                    sx={{
                        width: '100%',
                        '.MuiFormLabel-root': {
                            fontWeight: 'bold'
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '24px',
                        },
                        '&.Mui-focused .MuiSelect-select': {
                            backgroundColor: 'transparent',
                        },
                        fontSize: '14px',
                        fontWeight: 600,
                        height: '24px',
                        margin: '0px 0px 0px 0px',
                        padding: '3px 0px 0px 0px'
                    }}
                    disabled={isPortfolioSelectorDisabled(selectedPortfolio, availablePortfoliosForComparing)}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxWidth: `${selectorMaxWidth}px`,
                                maxHeight: '415px',
                            },
                            TransitionProps: {
                                timeout: 0,
                            },
                        },
                    }}
                >
                    {availablePortfoliosForComparing?.map((portfolio) => (
                        <MenuItem
                            key={portfolio.name}
                            value={portfolio.name}
                            sx={{
                                fontSize: 14,
                                paddingY: 0.5,
                                paddingX: 1,
                                minHeight: 32,

                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(0,0,0,0.08)',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                }
                            }}
                        >
                            <Tooltip
                                placement="left-start"
                                arrow
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            fontSize: 13,
                                            maxWidth: '500px'
                                        },
                                    },
                                }}
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [-5, 3],
                                                },
                                            },
                                        ],
                                    },
                                }}
                                title={
                                    portfolio.name
                                }
                            >
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        maxWidth: `${selectorMaxWidth}px`,

                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {portfolio.name}
                                </Box>
                            </Tooltip>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
}

function isPortfolioSelectorDisabled(selectedPortfolio: PortfolioModel | null,
    portfolios: PortfolioModel[]
): boolean {
    return portfolios
        ? portfolios.length === 0 || (!selectedPortfolio?.portfolioSettings?.useComparedPortfolio ?? false)
        : false;
}
